import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Plus, Trash2, Save, Package } from "lucide-react";

const COMMON_MATERIALS = [
  { name: "Solar panel 450W", unit: "ea" },
  { name: "Mounting rail", unit: "m" },
  { name: "MC4 connector", unit: "pair" },
  { name: "DC cable 6mm²", unit: "m" },
  { name: "Inverter 5kW", unit: "ea" },
  { name: "Battery pack", unit: "ea" },
];

/** MaterialsList — quick checklist + free-form. Saves to /work-orders/{id}/materials. */
export default function MaterialsList({ workOrderId, initial = [] }) {
  const [items, setItems] = useState(() => (initial || []).map((x) => ({ ...x, _id: x._id || `m_${Math.random().toString(36).slice(2, 9)}` })));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setItems((initial || []).map((x) => ({ ...x, _id: x._id || `m_${Math.random().toString(36).slice(2, 9)}` })));
    // intentionally key off workOrderId; initial is parent-stabilised
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workOrderId]);

  const add = (m) => setItems((arr) => [...arr, { name: m.name, qty: 1, unit: m.unit, _id: `m_${Math.random().toString(36).slice(2, 9)}` }]);
  const update = (i, key, v) => setItems((arr) => arr.map((it, idx) => idx === i ? { ...it, [key]: v } : it));
  const remove = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const payload = items.map(({ _id, ...rest }) => rest); // eslint-disable-line no-unused-vars
      await api.post(`/work-orders/${workOrderId}/materials`, { materials: payload });
      toast.success("Materials saved");
    } catch (err) {
      console.error("Materials save failed:", err);
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="card-pb p-4 bg-slate-50 dark:bg-slate-800/50" data-testid="materials-list">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-medium text-[#0A2540] dark:text-white flex items-center gap-2">
          <Package className="w-4 h-4 text-[#C9A356]" /> Materials used
        </h3>
        <button onClick={save} disabled={saving} className="btn-primary text-xs py-1.5 px-3" data-testid="save-materials">
          <Save className="w-3.5 h-3.5 mr-1" /> {saving ? "..." : "Save"}
        </button>
      </div>

      {/* Common items quick-add */}
      <div className="flex flex-wrap gap-1 mb-3">
        {COMMON_MATERIALS.map((m) => (
          <button key={m.name} onClick={() => add(m)} data-testid={`quick-mat-${m.name.replace(/\s+/g, "-").toLowerCase()}`}
                  className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-white dark:bg-slate-700 border border-[#E2E8F0] dark:border-slate-600 hover:border-[#C9A356] hover:text-[#C9A356] transition">
            <Plus className="w-3 h-3 inline mr-0.5" /> {m.name}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <p className="text-xs text-[#94A3B8] italic">Tap a quick-add chip above or add custom rows.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((it, i) => (
            <li key={it._id} className="flex items-center gap-2" data-testid={`mat-row-${i}`}>
              <input value={it.name} onChange={(e) => update(i, "name", e.target.value)} placeholder="Name"
                     className="flex-1 border border-[#E2E8F0] dark:border-slate-600 dark:bg-slate-800 dark:text-white rounded-md px-2 py-1 text-sm" />
              <input type="number" step="0.01" value={it.qty} onChange={(e) => update(i, "qty", parseFloat(e.target.value) || 0)} placeholder="Qty"
                     className="w-20 border border-[#E2E8F0] dark:border-slate-600 dark:bg-slate-800 dark:text-white rounded-md px-2 py-1 text-sm" />
              <input value={it.unit} onChange={(e) => update(i, "unit", e.target.value)} placeholder="ea"
                     className="w-16 border border-[#E2E8F0] dark:border-slate-600 dark:bg-slate-800 dark:text-white rounded-md px-2 py-1 text-sm" />
              <button onClick={() => remove(i)} className="text-red-500 hover:text-red-600" data-testid={`del-mat-${i}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <button onClick={() => add({ name: "", unit: "ea" })} data-testid="add-custom-mat"
              className="mt-3 text-xs text-[#C9A356] hover:text-[#DDBD77] inline-flex items-center gap-1">
        <Plus className="w-3 h-3" /> Add custom row
      </button>
    </div>
  );
}
